import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import { ArrowLeft, Save, Send, CheckCircle, Check } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useNavigate } from 'react-router-dom';

interface QuestionOption {
  id: string;
  label: string;
  value: string;
}

type ScaleType = 'fill' | 'likert' | 'frequency';

interface Question {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'radio' | 'checkbox' | 'number' | 'email' | 'url' | 'date' | 'range' | 'rating';
  title: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  scaleType?: ScaleType; // For rating type: 'fill' (0-100%), 'likert', 'frequency'
  followUpQuestion?: string; // Optional follow-up question for rating types
  options?: QuestionOption[];
  order: number;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: Question[];
}

interface EngagementPulseTemplate {
  id: string;
  name: string;
  title: string;
  description?: string;
  sections: Section[];
  settings: {
    requireAllQuestions: boolean;
    showProgressBar: boolean;
    thankYouMessage?: string;
    allowSaveProgress: boolean;
  };
  metadata?: {
    estimatedTimeMinutes?: number;
  };
}

interface FormData {
  [questionId: string]: any;
}

type FlowStep = 'selectSections' | 'selectQuestion' | 'answerQuestion';

const MAX_SECTIONS = 3;

const EngagementPulseCheck: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<EngagementPulseTemplate | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // New flow state
  const [currentStep, setCurrentStep] = useState<FlowStep>('selectSections');
  const [selectedSections, setSelectedSections] = useState<Section[]>([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [progressLoaded, setProgressLoaded] = useState(false);

  useEffect(() => {
    const init = async () => {
      await fetchTemplate();
      await checkIfAlreadyCompleted();
    };
    init();
  }, [currentUser]);

  // Load saved progress only after template is loaded (and only once)
  useEffect(() => {
    if (template && currentUser && !progressLoaded) {
      setProgressLoaded(true);
      loadSavedProgress();
    }
  }, [template, currentUser, progressLoaded]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const templatesRef = collection(db, 'engagementPulseTemplates');
      const activeQuery = query(templatesRef, where('isActive', '==', true));
      const snapshot = await getDocs(activeQuery);
      
      if (!snapshot.empty) {
        const templateDoc = snapshot.docs[0];
        const templateData = {
          id: templateDoc.id,
          ...templateDoc.data()
        } as EngagementPulseTemplate;
        
        setTemplate(templateData);
      } else {
        setError('No active engagement pulse survey found. Please contact your administrator.');
      }
    } catch (err: any) {
      console.error('Error fetching template:', err);
      setError('Failed to load survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkIfAlreadyCompleted = async () => {
    try {
      const { collection, getDocs, query, where, doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Get the user's current review cycle start timestamp
      const userRef = doc(db, 'users', currentUser?.uid || '');
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      // Prefer pulseCheckCycleStart (set when admin enables pulse check), then fall back to
      // reviewCycleStart timestamp, then the legacy reviewCycle date string.
      let reviewCycleStart: Date | null = null;
      if (userData?.pulseCheckCycleStart?.toDate) {
        reviewCycleStart = userData.pulseCheckCycleStart.toDate();
      } else if (userData?.reviewCycleStart?.toDate) {
        reviewCycleStart = userData.reviewCycleStart.toDate();
      } else if (userData?.reviewCycle) {
        // Parse as local date (append T00:00:00 to avoid UTC interpretation)
        reviewCycleStart = new Date(userData.reviewCycle + 'T00:00:00');
      }
      
      // Check for completed submissions
      const submissionsRef = collection(db, 'engagementPulseSubmissions');
      const userQuery = query(
        submissionsRef, 
        where('userId', '==', currentUser?.uid),
        where('status', '==', 'completed')
      );
      const snapshot = await getDocs(userQuery);
      
      if (!snapshot.empty) {
        // Check if any submission was made AFTER the current review cycle started
        const hasCurrentCycleSubmission = snapshot.docs.some(docSnap => {
          const submissionData = docSnap.data();
          const submittedAt = submissionData.submittedAt?.toDate();
          
          // If no review cycle set, treat any submission as complete
          if (!reviewCycleStart) {
            return true;
          }
          
          // Submission must be AFTER the cycle start (not >= to handle same-day re-adding)
          return submittedAt && submittedAt > reviewCycleStart;
        });
        
        if (hasCurrentCycleSubmission) {
          setSubmitted(true);
        }
      }
    } catch (err) {
      console.error('Error checking completion status:', err);
    }
  };

  const loadSavedProgress = async () => {
    if (!template) return;
    
    try {
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const submissionsRef = collection(db, 'engagementPulseSubmissions');
      const progressQuery = query(
        submissionsRef, 
        where('userId', '==', currentUser?.uid),
        where('status', '==', 'in_progress')
      );
      const snapshot = await getDocs(progressQuery);
      
      if (!snapshot.empty) {
        const savedData = snapshot.docs[0].data();
        
        if (savedData.responses) {
          setFormData(savedData.responses);
        }
        // Reconstruct full sections from template using saved section IDs
        if (savedData.selectedSectionIds && Array.isArray(savedData.selectedSectionIds)) {
          const reconstructedSections = savedData.selectedSectionIds
            .map((id: string) => template.sections.find(s => s.id === id))
            .filter((s: Section | undefined): s is Section => s !== undefined);
          setSelectedSections(reconstructedSections);
        } else if (savedData.selectedSections && Array.isArray(savedData.selectedSections)) {
          // Backward compatibility: handle old format where full sections were saved
          const sectionIds = savedData.selectedSections.map((s: any) => s.id);
          const reconstructedSections = sectionIds
            .map((id: string) => template.sections.find(s => s.id === id))
            .filter((s: Section | undefined): s is Section => s !== undefined);
          setSelectedSections(reconstructedSections);
        }
        if (savedData.completedSections) {
          setCompletedSections(savedData.completedSections);
        }
        if (savedData.currentSectionIndex !== undefined) {
          setCurrentSectionIndex(savedData.currentSectionIndex);
        }
        if (savedData.selectedQuestionId) {
          setSelectedQuestionId(savedData.selectedQuestionId);
        }
        if (savedData.currentStep) {
          setCurrentStep(savedData.currentStep);
        }
      }
    } catch (err) {
      console.error('Error loading saved progress:', err);
    }
  };

  const handleInputChange = (questionId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const toggleSectionSelection = (section: Section) => {
    setSelectedSections(prev => {
      const isSelected = prev.some(s => s.id === section.id);
      if (isSelected) {
        return prev.filter(s => s.id !== section.id);
      } else if (prev.length < MAX_SECTIONS) {
        return [...prev, section];
      }
      return prev;
    });
  };

  const confirmSectionSelection = () => {
    if (selectedSections.length === MAX_SECTIONS) {
      setCurrentStep('selectQuestion');
      setCurrentSectionIndex(0);
    }
  };

  const selectQuestion = (questionId: string) => {
    // Clear any previous answers for other questions in this section
    // This prevents orphaned answers when user changes their question selection
    const currentSection = selectedSections[currentSectionIndex];
    if (currentSection) {
      setFormData(prev => {
        const newData = { ...prev };
        for (const question of currentSection.questions) {
          // Clear answers for questions that are NOT the newly selected one
          if (question.id !== questionId) {
            delete newData[question.id];
            delete newData[`${question.id}_comments`];
            delete newData[`${question.id}_followup`];
          }
        }
        return newData;
      });
    }
    
    setSelectedQuestionId(questionId);
    setCurrentStep('answerQuestion');
  };

  const completeCurrentSection = () => {
    const currentSection = selectedSections[currentSectionIndex];
    
    // Mark section as completed
    setCompletedSections(prev => [...prev, currentSection.id]);
    
    // Move to next section or finish
    if (currentSectionIndex < selectedSections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      setSelectedQuestionId(null);
      setCurrentStep('selectQuestion');
    } else {
      // All sections complete - ready to submit
      submitSurvey();
    }
  };

  const goBackToQuestionSelection = () => {
    setSelectedQuestionId(null);
    setCurrentStep('selectQuestion');
  };

  const saveProgress = async () => {
    if (!template || !currentUser) return;
    
    try {
      setSaving(true);
      
      const { collection, doc, setDoc, updateDoc, getDocs, query, where, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const submissionsRef = collection(db, 'engagementPulseSubmissions');
      const progressQuery = query(
        submissionsRef, 
        where('userId', '==', currentUser.uid),
        where('status', '==', 'in_progress')
      );
      const snapshot = await getDocs(progressQuery);
      
      const submissionData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown',
        templateId: template.id,
        templateName: template.name,
        responses: formData,
        selectedSectionIds: selectedSections.map(s => s.id),
        completedSections: completedSections,
        currentSectionIndex: currentSectionIndex,
        selectedQuestionId: selectedQuestionId,
        currentStep: currentStep,
        status: 'in_progress',
        lastSaved: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      if (!snapshot.empty) {
        const existingDoc = snapshot.docs[0];
        const existingRef = doc(db, 'engagementPulseSubmissions', existingDoc.id);
        await updateDoc(existingRef, submissionData);
      } else {
        const submissionRef = doc(collection(db, 'engagementPulseSubmissions'));
        await setDoc(submissionRef, {
          ...submissionData,
          createdAt: serverTimestamp()
        });
      }
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving progress:', err);
    } finally {
      setSaving(false);
    }
  };

  const submitSurvey = async () => {
    if (!template || !currentUser) return;
    
    try {
      setSubmitting(true);
      
      const { collection, doc, setDoc, updateDoc, getDocs, getDoc, query, where, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const submissionsRef = collection(db, 'engagementPulseSubmissions');
      const progressQuery = query(
        submissionsRef, 
        where('userId', '==', currentUser.uid),
        where('status', '==', 'in_progress')
      );
      const snapshot = await getDocs(progressQuery);
      
      // Build question metadata for each answered question (one per section only)
      const answeredQuestions: { [questionId: string]: { title: string; sectionTitle: string; type: string; scaleType?: string; followUpQuestion?: string } } = {};
      const filteredResponses: { [key: string]: any } = {};
      
      for (const section of selectedSections) {
        // Find the ONE question in this section that was answered
        const answeredQuestion = section.questions.find(q => 
          formData[q.id] !== undefined && formData[q.id] !== ''
        );
        
        if (answeredQuestion) {
          // Only include defined values (Firebase doesn't accept undefined)
          const meta: { title: string; sectionTitle: string; type: string; scaleType?: string; followUpQuestion?: string } = {
            title: answeredQuestion.title,
            sectionTitle: section.title,
            type: answeredQuestion.type
          };
          if (answeredQuestion.scaleType) {
            meta.scaleType = answeredQuestion.scaleType;
          }
          if (answeredQuestion.followUpQuestion) {
            meta.followUpQuestion = answeredQuestion.followUpQuestion;
          }
          answeredQuestions[answeredQuestion.id] = meta;
          
          // Copy only this question's responses
          filteredResponses[answeredQuestion.id] = formData[answeredQuestion.id];
          if (formData[`${answeredQuestion.id}_comments`]) {
            filteredResponses[`${answeredQuestion.id}_comments`] = formData[`${answeredQuestion.id}_comments`];
          }
          if (formData[`${answeredQuestion.id}_followup`]) {
            filteredResponses[`${answeredQuestion.id}_followup`] = formData[`${answeredQuestion.id}_followup`];
          }
        }
      }
      
      // Get the user's current review cycle
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      const reviewCycle = userData?.reviewCycle || null;
      
      const submissionData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown',
        templateId: template.id,
        templateName: template.name,
        responses: filteredResponses,
        answeredQuestions: answeredQuestions,
        selectedSections: selectedSections.map(s => ({ id: s.id, title: s.title })),
        reviewCycle: reviewCycle, // Track which review cycle this submission belongs to
        status: 'completed',
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      if (!snapshot.empty) {
        const existingDoc = snapshot.docs[0];
        const existingRef = doc(db, 'engagementPulseSubmissions', existingDoc.id);
        await updateDoc(existingRef, submissionData);
      } else {
        const submissionRef = doc(collection(db, 'engagementPulseSubmissions'));
        await setDoc(submissionRef, {
          ...submissionData,
          createdAt: serverTimestamp()
        });
      }
      
      setSubmitted(true);
    } catch (err: any) {
      console.error('Error submitting survey:', err);
      setError('Failed to submit survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question: Question) => {
    const value = formData[question.id] || '';

    switch (question.type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            required={question.required}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            required={question.required}
            rows={4}
            className="min-h-[100px]"
          />
        );

      case 'rating':
        const scaleType = question.scaleType || 'fill';
        
        // Likert scale options
        if (scaleType === 'likert') {
          const likertOptions = [
            { value: '1', label: 'Strongly Disagree' },
            { value: '2', label: 'Disagree' },
            { value: '3', label: 'Neutral' },
            { value: '4', label: 'Agree' },
            { value: '5', label: 'Strongly Agree' }
          ];
          return (
            <div className="space-y-6">
              <div className="space-y-3">
                {likertOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleInputChange(question.id, option.value)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      value === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        value === option.value ? 'border-primary' : 'border-muted-foreground'
                      }`}>
                        {value === option.value && (
                          <div className="w-3 h-3 rounded-full bg-primary" />
                        )}
                      </div>
                      <span className="font-medium">{option.label}</span>
                    </div>
                  </div>
                ))}
              </div>
              {question.followUpQuestion && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{question.followUpQuestion}</label>
                  <Textarea
                    rows={3}
                    value={formData[`${question.id}_followup`] || ''}
                    onChange={(e) => handleInputChange(`${question.id}_followup`, e.target.value)}
                    placeholder="Enter your response..."
                    className="w-full"
                  />
                </div>
              )}
            </div>
          );
        }
        
        // Frequency scale options
        if (scaleType === 'frequency') {
          const frequencyOptions = [
            { value: '1', label: 'Never' },
            { value: '2', label: 'Rarely' },
            { value: '3', label: 'Sometimes' },
            { value: '4', label: 'Often' },
            { value: '5', label: 'Always' }
          ];
          return (
            <div className="space-y-6">
              <div className="space-y-3">
                {frequencyOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleInputChange(question.id, option.value)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      value === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        value === option.value ? 'border-primary' : 'border-muted-foreground'
                      }`}>
                        {value === option.value && (
                          <div className="w-3 h-3 rounded-full bg-primary" />
                        )}
                      </div>
                      <span className="font-medium">{option.label}</span>
                    </div>
                  </div>
                ))}
              </div>
              {question.followUpQuestion && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{question.followUpQuestion}</label>
                  <Textarea
                    rows={3}
                    value={formData[`${question.id}_followup`] || ''}
                    onChange={(e) => handleInputChange(`${question.id}_followup`, e.target.value)}
                    placeholder="Enter your response..."
                    className="w-full"
                  />
                </div>
              )}
            </div>
          );
        }
        
        // Fill scale (0-100% progress bar style)
        const fillValue = value || 50;
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="text-center">
                <span className="text-2xl font-bold text-primary">{fillValue}%</span>
              </div>
              <div className="relative px-2">
                <div className="absolute inset-0 px-2 flex items-center pointer-events-none">
                  <div className="w-full h-3 bg-muted rounded-lg overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-lg"
                      style={{ width: `${fillValue}%` }}
                    />
                  </div>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={fillValue}
                  onChange={(e) => handleInputChange(question.id, parseInt(e.target.value))}
                  required={question.required}
                  className="relative w-full h-3 bg-transparent rounded-lg appearance-none cursor-pointer z-10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-background [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-background [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                />
              </div>
            </div>
            {question.followUpQuestion && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{question.followUpQuestion}</label>
                <Textarea
                  rows={3}
                  value={formData[`${question.id}_followup`] || ''}
                  onChange={(e) => handleInputChange(`${question.id}_followup`, e.target.value)}
                  placeholder="Enter your response..."
                  className="w-full"
                />
              </div>
            )}
          </div>
        );

      case 'radio':
        return (
          <RadioGroup
            value={value}
            onValueChange={(value) => handleInputChange(question.id, value)}
            required={question.required}
          >
            {question.options?.map((option) => (
              <div key={option.id} className="flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={option.id} />
                <Label htmlFor={option.id} className="cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        const checkboxValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <div key={option.id} className="flex items-center space-x-2">
                <Checkbox
                  id={option.id}
                  checked={checkboxValues.includes(option.value)}
                  onCheckedChange={(checked) => {
                    const newValues = checked
                      ? [...checkboxValues, option.value]
                      : checkboxValues.filter((v: string) => v !== option.value);
                    handleInputChange(question.id, newValues);
                  }}
                />
                <Label htmlFor={option.id} className="cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        );

      case 'select':
        return (
          <Select
            value={value}
            onValueChange={(value) => handleInputChange(question.id, value)}
            required={question.required}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an option..." />
            </SelectTrigger>
            <SelectContent>
              {question.options?.map((option) => (
                <SelectItem key={option.id} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            required={question.required}
          />
        );
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Submitted state
  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  Engagement Pulse Check
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="text-center py-12">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
              <div 
                className="text-muted-foreground mb-6 prose prose-sm max-w-none [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                dangerouslySetInnerHTML={{ 
                  __html: template?.settings.thankYouMessage || 'Your engagement pulse survey has been submitted successfully.' 
                }}
              />
              <Button onClick={() => navigate('/dashboard')}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // No template state
  if (!template) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  Engagement Pulse Check
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="text-center py-12">
              <h2 className="text-xl font-bold mb-2">Survey Not Available</h2>
              <p className="text-muted-foreground mb-6">
                {error || 'No active engagement pulse survey is currently available.'}
              </p>
              <Button onClick={() => navigate('/dashboard')}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const currentSection = selectedSections[currentSectionIndex];
  const currentQuestion = currentSection?.questions.find(q => q.id === selectedQuestionId);
  const progress = ((completedSections.length) / MAX_SECTIONS) * 100;

  // Safety check: if we're in a step that requires sections but don't have them, or index is invalid
  const needsSections = currentStep === 'selectQuestion' || currentStep === 'answerQuestion';
  const sectionsInvalid = selectedSections.length === 0 || (needsSections && !currentSection);
  const questionInvalid = currentStep === 'answerQuestion' && currentSection && !currentQuestion;
  
  // If we're in answerQuestion but question not found, go back to question selection
  if (questionInvalid) {
    setCurrentStep('selectQuestion');
    setSelectedQuestionId(null);
    return null; // Will re-render with selectQuestion step
  }
  
  if (needsSections && sectionsInvalid) {
    // This can happen if saved progress couldn't restore sections properly
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  {template.title}
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="text-center py-12">
              <h2 className="text-xl font-bold mb-2">Session Expired</h2>
              <p className="text-muted-foreground mb-6">
                Your previous progress couldn't be restored. Please start again.
              </p>
              <Button onClick={() => {
                setCurrentStep('selectSections');
                setSelectedSections([]);
                setCompletedSections([]);
                setCurrentSectionIndex(0);
                setFormData({});
              }}>
                Start Over
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Step 1: Section Selection
  if (currentStep === 'selectSections') {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  {template.title}
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          {error && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-6 max-w-4xl mx-auto">
              {error}
            </div>
          )}

          <div className="max-w-4xl mx-auto">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Choose {MAX_SECTIONS} Topics to Reflect On</CardTitle>
                {template.description && (
                  <div 
                    className="text-sm text-muted-foreground prose prose-sm max-w-none [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                    dangerouslySetInnerHTML={{ __html: template.description }}
                  />
                )}
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6">
                  Select {MAX_SECTIONS} sections that you'd like to reflect on. You'll choose one question from each section to answer.
                </p>
                
                <div className="text-sm font-medium mb-4">
                  Selected: {selectedSections.length} / {MAX_SECTIONS}
                </div>

                <TooltipProvider delayDuration={300}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {template.sections
                      .sort((a, b) => a.order - b.order)
                      .map((section) => {
                        const isSelected = selectedSections.some(s => s.id === section.id);
                        const isDisabled = !isSelected && selectedSections.length >= MAX_SECTIONS;
                        
                        return (
                          <Tooltip key={section.id}>
                            <TooltipTrigger asChild>
                              <div
                                onClick={() => !isDisabled && toggleSectionSelection(section)}
                                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-primary bg-primary/5'
                                    : isDisabled
                                    ? 'border-muted bg-muted/20 opacity-50 cursor-not-allowed'
                                    : 'border-border hover:border-primary/50 hover:bg-muted/30'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <h3 className="font-semibold mb-1">{section.title}</h3>
                                    {section.description && (
                                      <div 
                                        className="text-sm text-muted-foreground prose prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0"
                                        dangerouslySetInnerHTML={{ __html: section.description }}
                                      />
                                    )}
                                    <p className="text-xs text-muted-foreground mt-2">
                                      {section.questions.length} question{section.questions.length !== 1 ? 's' : ''} available
                                    </p>
                                  </div>
                                  {isSelected && (
                                    <div className="ml-3 flex-shrink-0">
                                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                        <Check className="h-4 w-4 text-primary-foreground" />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs p-3">
                              <p className="font-semibold text-sm mb-2">Available Questions:</p>
                              <ul className="space-y-1">
                                {section.questions
                                  .sort((a, b) => a.order - b.order)
                                  .map((question) => (
                                    <li key={question.id} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                      <span className="text-primary mt-0.5">•</span>
                                      <span>{question.title}</span>
                                    </li>
                                  ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                  </div>
                </TooltipProvider>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={confirmSectionSelection}
                disabled={selectedSections.length !== MAX_SECTIONS}
                className="flex items-center gap-2"
              >
                Continue
                <ArrowLeft className="h-4 w-4 rotate-180" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Step 2: Question Selection
  if (currentStep === 'selectQuestion' && currentSection) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  {template.title}
                </h1>
                <p className="text-muted-foreground">
                  Section {currentSectionIndex + 1} of {MAX_SECTIONS}: {currentSection.title}
                </p>
              </div>
            </div>
            <div className="text-right">
              {template.settings.showProgressBar && (
                <div className="w-32 h-2 bg-muted rounded-full">
                  <div 
                    className="h-2 bg-primary rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Progress indicators */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {selectedSections.map((section, idx) => (
                <div 
                  key={section.id}
                  className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                    completedSections.includes(section.id)
                      ? 'bg-green-100 text-green-700'
                      : idx === currentSectionIndex
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {completedSections.includes(section.id) ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <span className="w-4 h-4 flex items-center justify-center text-xs">{idx + 1}</span>
                  )}
                  <span className="hidden sm:inline">{section.title}</span>
                </div>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Choose a Question to Answer</CardTitle>
                {currentSection.description && (
                  <div 
                    className="text-sm text-muted-foreground prose prose-sm max-w-none [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1"
                    dangerouslySetInnerHTML={{ __html: currentSection.description }}
                  />
                )}
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6">
                  Select one question from this section that you'd like to reflect on.
                </p>

                <div className="space-y-3">
                  {currentSection.questions
                    .sort((a, b) => a.order - b.order)
                    .map((question) => (
                      <div
                        key={question.id}
                        onClick={() => selectQuestion(question.id)}
                        className="p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/30 cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium mb-1">{question.title}</h4>
                          {question.type === 'rating' && (
                            <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                              {question.scaleType === 'likert' ? 'Likert Scale' :
                               question.scaleType === 'frequency' ? 'Frequency Scale' :
                               'Fill Scale (0-100%)'}
                            </span>
                          )}
                        </div>
                        {question.description && (
                          <div 
                            className="text-sm text-muted-foreground prose prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0"
                            dangerouslySetInnerHTML={{ __html: question.description }}
                          />
                        )}
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between mt-8">
              <div className="flex items-center gap-4">
                {currentSectionIndex > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Go back to previous section's question selection
                      // Remove the previous section from completed if we're going back
                      const prevSection = selectedSections[currentSectionIndex - 1];
                      setCompletedSections(prev => prev.filter(id => id !== prevSection.id));
                      setCurrentSectionIndex(prev => prev - 1);
                    }}
                  >
                    Previous Section
                  </Button>
                )}
                {currentSectionIndex === 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep('selectSections')}
                  >
                    Change Sections
                  </Button>
                )}
                {template.settings.allowSaveProgress && (
                  <Tooltip open={showSaveSuccess}>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="secondary" 
                        onClick={saveProgress}
                        disabled={saving}
                        className="flex items-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        {saving ? 'Saving...' : 'Save Progress'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-green-600 text-white border-green-600">
                      <p>Progress saved!</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Step 3: Answer Question
  if (currentStep === 'answerQuestion' && currentSection && currentQuestion) {
    const hasAnswer = formData[currentQuestion.id] !== undefined && formData[currentQuestion.id] !== '';

    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  {template.title}
                </h1>
                <p className="text-muted-foreground">
                  Section {currentSectionIndex + 1} of {MAX_SECTIONS}: {currentSection.title}
                </p>
              </div>
            </div>
            <div className="text-right">
              {template.settings.showProgressBar && (
                <div className="w-32 h-2 bg-muted rounded-full">
                  <div 
                    className="h-2 bg-primary rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Progress indicators */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {selectedSections.map((section, idx) => (
                <div 
                  key={section.id}
                  className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                    completedSections.includes(section.id)
                      ? 'bg-green-100 text-green-700'
                      : idx === currentSectionIndex
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {completedSections.includes(section.id) ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <span className="w-4 h-4 flex items-center justify-center text-xs">{idx + 1}</span>
                  )}
                  <span className="hidden sm:inline">{section.title}</span>
                </div>
              ))}
            </div>

            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    {currentQuestion.title}
                    {currentQuestion.required && (
                      <span className="text-destructive text-sm">*</span>
                    )}
                  </CardTitle>
                  {currentQuestion.type === 'rating' && (
                    <span className="flex-shrink-0 px-3 py-1 text-sm font-medium bg-primary/10 text-primary rounded-full">
                      {currentQuestion.scaleType === 'likert' ? 'Likert Scale' :
                       currentQuestion.scaleType === 'frequency' ? 'Frequency Scale' :
                       'Fill Scale (0-100%)'}
                    </span>
                  )}
                </div>
                {currentQuestion.description && (
                  <div 
                    className="text-sm text-muted-foreground prose prose-sm max-w-none [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-1 mt-2"
                    dangerouslySetInnerHTML={{ __html: currentQuestion.description }}
                  />
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  {renderQuestion(currentQuestion)}
                </div>

                {/* Additional Comments */}
                <div className="space-y-2 pt-4 border-t">
                  <label className="text-sm font-medium text-muted-foreground">
                    Additional Comments (Optional)
                  </label>
                  <Textarea
                    rows={3}
                    value={formData[`${currentQuestion.id}_comments`] || ''}
                    onChange={(e) => handleInputChange(`${currentQuestion.id}_comments`, e.target.value)}
                    placeholder="Add any additional thoughts or context..."
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between mt-8">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={goBackToQuestionSelection}
                >
                  Choose Different Question
                </Button>
                {template.settings.allowSaveProgress && (
                  <Tooltip open={showSaveSuccess}>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="secondary" 
                        onClick={saveProgress}
                        disabled={saving}
                        className="flex items-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        {saving ? 'Saving...' : 'Save Progress'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-green-600 text-white border-green-600">
                      <p>Progress saved!</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <Button
                onClick={completeCurrentSection}
                disabled={!hasAnswer || submitting}
                className="flex items-center gap-2"
              >
                {currentSectionIndex < selectedSections.length - 1 ? (
                  <>
                    Next Section
                    <ArrowLeft className="h-4 w-4 rotate-180" />
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    {submitting ? 'Submitting...' : 'Submit Survey'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Fallback - this should never be reached
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-primary">
                Engagement Pulse Check
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="text-center py-12">
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">
              We couldn't display the survey. Please try starting over.
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              Debug: step={currentStep}, sections={selectedSections.length}, index={currentSectionIndex}
            </p>
            <Button onClick={() => {
              setCurrentStep('selectSections');
              setSelectedSections([]);
              setCompletedSections([]);
              setCurrentSectionIndex(0);
              setSelectedQuestionId(null);
              setFormData({});
            }}>
              Start Over
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EngagementPulseCheck;
